"use client"

import React, { useState, type ChangeEvent, type FormEvent, useEffect } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faEnvelope,
  faLock,
  faUser,
  faPhone,
  faEye,
  faEyeSlash,
  faMapMarkerAlt,
  faBuilding,
  faMale,
  faFemale,
  faBriefcase,
  faIdCard,
  faReceipt,
  faCreditCard,
  faFileAlt,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons"
import { FaTicketAlt } from "react-icons/fa"
import { HiIdentification } from "react-icons/hi"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { motion, AnimatePresence } from "framer-motion"
import logo from '../image/alo_logo.png';
import test1 from '../image/image.gif';
import { getParentId, registerUser } from '../services/Registre_service';
import { fetchPostalCodeDetails } from '../utils/functions/fetchPostalCodeDetails';
import { isValidRcs } from '../utils/functions/validateRCSNumber';
import { isValidPostalCode } from '../utils/functions/validatePostalCode';
import axios from "../api/axios"
import * as fetchAPI from 'axios'
import '../css/registre.css';
import { registerUserWithFiles } from "../services/User_service"
import ProfessionalDocumentUpload from "./register/ProfessionalDocumentUpload"
import RibModal from "./client/avoir/RibModal"
import { Avatar, Upload, Button, Tooltip, Typography } from "antd"
import { UserOutlined, CameraOutlined } from "@ant-design/icons"

interface FormData {
  civility: string
  email: string
  name: string
  number: string
  firstname: string
  password: string
  confirmPassword: string
  adresse: string
  postal_code: string
  country: string
  code_parrainage: string
  code_SIREN: string
  raison_social: string
  code_NAF: string
  tv_intra: string
  user_type: "personne" | "entreprise" | "professionnel"
  profession: string
  profession_other: string
  profession_is_other?: boolean
  kbis_file?: File | null
  identity_recto_file?: File | null
  identity_verso_file?: File | null
  identity_document_type?: 'cin' | 'passeport'
  rib_file?: File | null
  rib?: string
  bic?: string
  profile_picture?: File | null
  profile_picture_preview?: string
  show_in_directory?: boolean
  rgpd_directory_consent?: boolean
}

interface FormErrors {
  [key: string]: string | boolean;
}

const formatRIB = (rib: string) => {
  const clean = rib.replace(/\s+/g, "");
  if (clean.length !== 23) return rib;
  const bank = clean.slice(0, 5);
  const guichet = clean.slice(5, 10);
  const compte = clean.slice(10, 21);
  const cle = clean.slice(21);
  return `${bank} ${guichet} ${compte} ${cle}`;
};

// ⭐ TOUTES LES PROFESSIONS (pour Particulier et Entreprise)
const ALL_PROFESSION_OPTIONS: Record<string, string[]> = {
  "Professions juridiques": [
    "Administrateur / Mandataire judiciaire",
    "Avocat",
    "Clerc / Formaliste de notaire",
    "Greffier / Assistant de greffe",
    "Huissier de justice / Commissaire de justice",
    "Juriste d'entreprise",
    "Notaire",
  ],
  "Professions comptables": [
    "Cabinet comptable / Conseil en gestion",
    "Commissaire aux comptes",
    "Expert-comptable",
    "Gestionnaire de paie / RAF",
  ],
  "Créateurs et dirigeants d'entreprise": [
    "Dirigeant d'entreprise",
    "Entrepreneur individuel / Auto-entrepreneur",
    "Office manager / Responsable administratif",
  ],
  "Intermédiaires et formalistes": [
    "Agence de communication / Publicité",
    "Cabinet de formalités juridiques",
    "Formaliste en annonces légales",
    "Secrétaire juridique indépendant",
    "Société de domiciliation",
  ],
  "Autres professions": [
    "Administration / Assistance administrative",
    "Ressources humaines",
    "Responsable juridique",
    "Consultant indépendant",
    "Freelance",
  ],
  "Autre": [
    "Autre (à préciser)"
  ]
}

// ⭐ PROFESSIONNELS DU DROIT UNIQUEMENT (pour Professionnel)
const PRO_PROFESSION_OPTIONS: Record<string, string[]> = {
  "Professions juridiques": [
    "Notaire",
    "Avocat",
    "Expert-comptable",
    "Commissaire de justice",
    "Comptable",
    "Juriste freelance",
    "Autre (autre profession juridique)",
  ]
}

const PROFESSIONS_AUTO_PRO = [
  "Notaire",
  "Avocat",
  "Expert-comptable",
  "Commissaire de justice",
  "Comptable",
  "Juriste freelance"
];

const generateProfessionOptions = (userType: "personne" | "entreprise" | "professionnel") => {
  // ⭐ Choisir la liste en fonction du type d'utilisateur
  const optionsList = userType === "professionnel" ? PRO_PROFESSION_OPTIONS : ALL_PROFESSION_OPTIONS;
  
  const options: { value: string; label: string; isCategory?: boolean; category?: string }[] = []

  Object.entries(optionsList).forEach(([category, subcategories]) => {
    options.push({
      value: `__category__${category}`,
      label: category,
      isCategory: true,
    })

    subcategories.forEach(sub => {
      const value = sub.includes('Autre') ? 'Autre' : sub
      options.push({
        value,
        label: `  ${sub}`,
        isCategory: false,
        category,
      })
    })
  })

  return options
}

interface FormFieldProps {
  icon: React.ReactNode
  type: string
  id: string
  placeholder: string
  value: string
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  required?: boolean
  className?: string
  onPaste?: (e: React.ClipboardEvent) => void
  onCopy?: (e: React.ClipboardEvent) => void
  onBlur?: () => void
  pattern?: string
  inputMode?: "text" | "tel" | "email" | "numeric" | "decimal" | "search" | "url" | "none"
  style?: React.CSSProperties
  label?: string
  error?: boolean
  errorMessage?: string
}

const FormField: React.FC<FormFieldProps> = ({
  icon,
  type,
  id,
  placeholder,
  value,
  onChange,
  required = false,
  className = "col-md-6 mb-3",
  onPaste,
  onCopy,
  onBlur,
  pattern,
  inputMode,
  style,
  label,
  error = false,
  errorMessage
}) => (
  <div className={className}>
    {label && (
      <label htmlFor={id} className="form-label mb-1">
        <span className="field-icon">{icon}</span>
        {label}{required && <span className="required-star"> *</span>}
      </label>
    )}
    <div className="input-group">
      <input
        type={type}
        className={`form-control ${error ? 'is-invalid' : ''}`}
        id={id}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        onPaste={onPaste}
        onCopy={onCopy}
        onBlur={onBlur}
        pattern={pattern}
        inputMode={inputMode}
        style={{
          ...style,
          borderColor: error ? '#dc3545' : undefined,
          boxShadow: error ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)' : undefined
        }}
      />
    </div>
    {error && errorMessage && (
      <div className="invalid-feedback d-block">
        {errorMessage}
      </div>
    )}
  </div>
)

interface FormSelectProps {
  icon: React.ReactNode
  id: string
  value: string
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void
  onBlur?: () => void
  options: Array<{ value: string; label: string; disabled?: boolean; isCategory?: boolean; category?: string }>
  placeholder: string
  required?: boolean
  className?: string
  label?: string
  error?: boolean
  errorMessage?: string
}

const FormSelect: React.FC<FormSelectProps> = ({
  icon,
  id,
  value,
  onChange,
  onBlur,
  options,
  placeholder,
  required = false,
  className = "col-md-6 mb-3",
  label,
  error = false,
  errorMessage
}) => (
  <div className={className}>
    {label && (
      <label htmlFor={id} className="form-label mb-1">
        <span className="field-icon">{icon}</span>
        {label}{required && <span className="required-star"> *</span>}
      </label>
    )}
    <div className="input-group">
      <select
        className={`form-select ${error ? 'is-invalid' : ''}`}
        id={id}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        required={required}
        style={{
          borderColor: error ? '#dc3545' : undefined,
          boxShadow: error ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)' : undefined
        }}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((option, index) => (
          <option
            key={index}
            value={option.value}
            disabled={option.disabled || option.isCategory}
            data-category={option.category || ""}
            style={{
              fontWeight: option.isCategory ? 'bold' : 'normal',
              backgroundColor: option.isCategory ? '#f0f8fa' : 'white',
              color: option.isCategory ? '#2A919D' : 'inherit', 
              borderLeft: option.isCategory ? `3px solid #4bb0c0` : 'none'
            }}
          >
            {option.label}
          </option>
        ))}
      </select>
    </div>
    {error && errorMessage && (
      <div className="invalid-feedback d-block">
        {errorMessage}
      </div>
    )}
  </div>
)

interface PasswordFieldProps {
  icon: React.ReactNode
  id: string
  placeholder: string
  value: string
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  showPassword: boolean
  toggleVisibility: () => void
  required?: boolean
  className?: string
  onPaste?: (e: React.ClipboardEvent) => void
  onCopy?: (e: React.ClipboardEvent) => void
  onBlur?: () => void
  label?: string
  error?: boolean
  errorMessage?: string
}

const PasswordField: React.FC<PasswordFieldProps> = ({
  icon,
  id,
  placeholder,
  value,
  onChange,
  showPassword,
  toggleVisibility,
  required = false,
  className = "col-md-6 mb-3",
  onPaste,
  onCopy,
  onBlur,
  label,
  error = false,
  errorMessage
}) => (
  <div className={className}>
    {label && (
      <label htmlFor={id} className="form-label mb-1">
        <span className="field-icon">{icon}</span>
        {label}{required && <span className="required-star"> *</span>}
      </label>
    )}
    <div className="input-group">
      <input
        className={`form-control ${error ? 'is-invalid' : ''}`}
        type={showPassword ? "text" : "password"}
        placeholder={placeholder}
        id={id}
        value={value}
        onChange={onChange}
        onPaste={onPaste}
        onCopy={onCopy}
        onBlur={onBlur}
        required={required}
        style={{
          borderColor: error ? '#dc3545' : undefined,
          boxShadow: error ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)' : undefined
        }}
      />
      <button
        className={`btn btn-outline-secondary ${error ? 'border-danger' : ''}`}
        type="button"
        onClick={toggleVisibility}
        style={{
          borderColor: error ? '#dc3545' : undefined
        }}
      >
        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
      </button>
    </div>
    {error && errorMessage && (
      <div className="invalid-feedback d-block">
        {errorMessage}
      </div>
    )}
  </div>
)

const Register: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    civility: "",
    email: "",
    name: "",
    number: "",
    firstname: "",
    password: "",
    confirmPassword: "",
    adresse: "",
    postal_code: "",
    country: "",
    code_parrainage: "",
    code_SIREN: "",
    raison_social: "",
    code_NAF: "",
    tv_intra: "",
    user_type: "personne",
    profession: "",
    profession_other: "",
    profession_is_other: false,
    kbis_file: null,
    identity_recto_file: null,
    identity_verso_file: null,
    identity_document_type: 'cin',
    rib_file: null,
    rib: undefined,
    bic: undefined,
    profile_picture: null,
    show_in_directory: false,
    rgpd_directory_consent: false,
  })
  const [formErrors, setFormErrors] = useState<FormErrors>({})
  const [showPassword, setShowPassword] = useState(false)
  const [showPassword2, setShowPassword2] = useState(false)
  const [userType, setUserType] = useState<"personne" | "entreprise" | "professionnel">("personne")
  const [statusLoading, setStatusLoading] = useState(false)
  const [ribModalVisible, setRibModalVisible] = useState(false)
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set())
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [cities, setCities] = useState<string[]>([])
  const [gender, setGender] = useState<"male" | "female">("male")
  const [showProOption, setShowProOption] = useState(false);

  const validatePhone = (value: string) => {
    const frenchPhoneRegex = /^(0\d{9}|\+33\d{9})$/;
    const europeanPhoneRegex = /^\+[1-9]\d{6,14}$/;
    const cleanValue = value.replace(/\s+/g, "");
    return frenchPhoneRegex.test(cleanValue) || europeanPhoneRegex.test(cleanValue);
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // ⭐ Génère les options de profession en fonction du type d'utilisateur
  const professionOptions = generateProfessionOptions(userType);

  useEffect(() => {
    const ref = searchParams.get("ref")
    if (ref) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        code_parrainage: ref,
      }))
    }
  }, [searchParams])

  // ⭐ Réinitialiser la profession quand le type d'utilisateur change
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      profession: "",
      profession_other: "",
      profession_is_other: false
    }));
  }, [userType]);

  const handleRCSInput = async (event: ChangeEvent<HTMLInputElement>) => {
    const rcs = event.target.value
    if (/^\d{0,9}$/.test(rcs)) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        code_SIREN: rcs,
      }))

      if (rcs.length === 9 || rcs.length === 0) {
        validateField('code_SIREN', rcs);
      }

      if (isValidRcs(rcs)) {
        try {
          const response = await axios(`/rne/${rcs}`)
          const nafCode = await axios(`/rne/naf/${rcs}`)
          if (response) {
            const postalCode = response.data.data.postalCode

            setFormData((prevFormData) => ({
              ...prevFormData,
              raison_social: response.data.data.companyName,
              postal_code: postalCode,
              adresse: response.data.data.address,
              country: response.data.data.cities?.[0] || "",
              code_NAF: nafCode.data.data.naf_code,
              tv_intra: nafCode.data.data.vat_number,
            }))

            if (postalCode && isValidPostalCode(postalCode)) {
              await fetchCities(postalCode)
            } else {
              const companyCities = response.data.data.cities || []
              setCities(companyCities)
            }
          }
        } catch (error) {
          console.error(error)
        }
      }
    }
  }

  const fetchCities = async (postal_code: string) => {
    try {
      const postalDetails = await fetchPostalCodeDetails(postal_code)
      setCities(postalDetails?.cities || [])
      setFormData((prevFormData) => ({
        ...prevFormData,
        country: postalDetails?.cities?.[0] || ""
      }));
    } catch (error) {
      console.log(error)
    }
  }

  const handleBlur = (fieldId: string) => {
    setTouchedFields(prev => new Set([...prev, fieldId]));
    validateField(fieldId, formData[fieldId as keyof typeof formData] as string);
  };

  const validateField = (fieldId: string, value: string) => {
    let error = '';

    switch (fieldId) {
      case 'email':
        if (!value.trim()) {
          error = 'L\'email est requis';
        } else if (!validateEmail(value)) {
          error = 'Format d\'email invalide';
        }
        break;

      case 'number':
        if (!value.trim()) {
          error = 'Le téléphone est requis';
        } else if (!validatePhone(value)) {
          error = 'Numéro invalide. Doit être français ou européen.';
        }
        break;

      case 'password':
        if (!value.trim()) {
          error = 'Le mot de passe est requis';
        } else if (value.length < 6) {
          error = 'Le mot de passe doit contenir au moins 6 caractères';
        }
        break;

      case 'confirmPassword':
        if (!value.trim()) {
          error = 'La confirmation du mot de passe est requise';
        } else if (formData.password !== value) {
          error = 'Les mots de passe ne correspondent pas';
        }
        break;

      case 'code_SIREN':
        if ((userType === 'entreprise' || userType === 'professionnel') && !value.trim()) {
          error = 'Le numéro SIREN est requis';
        } else if (value && !/^\d{9}$/.test(value)) {
          error = 'Le numéro SIREN doit contenir 9 chiffres';
        }
        break;

      case 'postal_code':
        if (!value.trim()) {
          error = 'Le code postal est requis';
        } else if (!/^\d{5}$/.test(value)) {
          error = 'Le code postal doit contenir 5 chiffres';
        }
        break;

      case 'name':
      case 'firstname':
      case 'adresse':
      case 'country':
      case 'civility':
      case 'profession':
        if (!value.trim()) {
          error = 'Ce champ est requis';
        }
        break;

      case 'profession_other':
        if (formData.profession_is_other && !value.trim()) {
          error = 'Veuillez préciser votre profession';
        }
        break;

      case 'raison_social':
        if ((userType === 'entreprise' || userType === 'professionnel') && !value.trim()) {
          error = 'Le nom de la société est requis';
        }
        break;

      case 'tv_intra':
        if ((userType === 'entreprise' || userType === 'professionnel') && !value.trim()) {
          error = 'La TVA intracommunautaire est requise';
        }
        break;
    }

    setFormErrors(prev => ({
      ...prev,
      [fieldId]: error
    }));

    return !error;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
    const { id, value } = e.target as HTMLInputElement | HTMLSelectElement;

    if (id === "profession") {
      const select = e.target as HTMLSelectElement
      const rawValue = select.value
      
      setFormData((prev) => ({
        ...prev,
        profession: rawValue,
        profession_is_other: rawValue === 'Autre',
      }));
    } else if (id === "postal_code") {
          if (/^\d{0,5}$/.test(value)) {
            setFormData((prevFormData) => ({
              ...prevFormData,
              [id]: value,
            }));

            if (isValidPostalCode(value)) {
              fetchCities(value);
            } else {
              setCities([]);
            }
          }
        } else if (id === "number") {
          if (/^[0-9+]*$/.test(value)) {
            setFormData((prevFormData) => ({
              ...prevFormData,
              [id]: value,
            }));
          }
        } else {
          setFormData((prevFormData) => ({
            ...prevFormData,
            [id]: value,
          }));
        }

        if (touchedFields.has(id)) {
          validateField(id, value);
        }
      };

  const handlePasswordConfirmation = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target
    setFormData((prevFormData) => ({
      ...prevFormData,
      [id]: value,
    }));

    if (touchedFields.has(id)) {
      validateField(id, value);
    }
  }

  const validatePasswordMatch = () => {
    if (formData.confirmPassword && formData.password !== formData.confirmPassword) {
      toast.warning("Les mots de passe ne correspondent pas")
      setFormErrors(prev => ({
        ...prev,
        confirmPassword: 'Les mots de passe ne correspondent pas'
      }));
    } else {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.confirmPassword;
        return newErrors;
      });
    }
  }

  const preventCopyPaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    toast.warning("Le copier-coller n'est pas autorisé pour ce champ")
  }

  const handleProfessionalDocuments = (documents: {
    kbisFile: File | null;
    identityRecto: File | null;
    identityVerso: File | null;
    identityType: 'cin' | 'passeport';
    ribFile: File | null;
  }) => {
    setFormData(prev => ({
      ...prev,
      kbis_file: documents.kbisFile,
      identity_recto_file: documents.identityRecto,
      identity_verso_file: documents.identityVerso,
      identity_document_type: documents.identityType,
      rib_file: documents.ribFile,
    }))
  }

  const handleRibConfirm = (rib: string, bic?: string) => {
    setFormData(prev => ({
      ...prev,
      rib: rib,
      bic: bic
    }))
    setRibModalVisible(false)
  }

  const handleUserTypeChange = (type: "personne" | "entreprise" | "professionnel") => {
    setUserType(type);
    
    if (type === "professionnel") {
      setShowProOption(true);
    } 
    else if (type === "entreprise") {
      setShowProOption(true);
    }
    else {
      setShowProOption(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validation (inchangée)
    const allFields = [
      'civility', 'email', 'name', 'number', 'firstname', 'password', 'confirmPassword',
      'adresse', 'postal_code', 'country', 'profession'
    ];

    if (userType === 'entreprise' || userType === 'professionnel') {
      allFields.push('code_SIREN', 'raison_social', 'tv_intra');
    }

    if (formData.profession_is_other) {
      allFields.push('profession_other');
    }

    const newTouchedFields = new Set([...touchedFields, ...allFields]);
    setTouchedFields(newTouchedFields);

    let isValid = true;
    allFields.forEach(field => {
      const value = formData[field as keyof typeof formData] as string;
      if (!validateField(field, value)) {
        isValid = false;
      }
    });

    if (!isValid) {
      toast.error("Veuillez corriger les erreurs dans le formulaire");
      return;
    }

    if (!validateForm()) return;

    setStatusLoading(true);

    try {
      let idParent: number | undefined;
      if (formData.code_parrainage) {
        idParent = (await getParentId(formData.code_parrainage)) ?? undefined;
      }

      const professionFinal = formData.profession_is_other ? formData.profession_other : formData.profession;

      if (userType === "professionnel") {
        // ============================================
        // PROFESSIONNEL - TOUJOURS FormData
        // ============================================
        const formDataToSend = new FormData();

        Object.keys(formData).forEach(key => {
          const value = formData[key as keyof typeof formData];
          if (value !== null && value !== undefined &&
            key !== 'kbis_file' &&
            key !== 'identity_recto_file' &&
            key !== 'profile_picture_preview' &&
            key !== 'identity_verso_file' &&
            key !== 'identity_document_type' &&
            key !== 'profession_is_other' &&
            key !== 'profession_other' &&
            key !== 'profession' &&
            key !== 'rib_file' &&
            key !== 'rib' && key !== 'bic' &&
            key !== 'profile_picture' && key !== 'confirmPassword') {
            formDataToSend.append(key, String(value));
          }
        });

        formDataToSend.append('profession', professionFinal);
        formDataToSend.append('profession_is_other', String(!!formData.profession_is_other));
        formDataToSend.append('user_type', 'professionnel');
        formDataToSend.append('show_in_directory', formData.show_in_directory ? 'true' : 'false');

        if (idParent) formDataToSend.append('idParent', String(idParent));

        if (formData.rib) formDataToSend.append('account_number', formData.rib);
        if (formData.bic) formDataToSend.append('bic_swift', formData.bic);

        if (formData.kbis_file) formDataToSend.append('kbis_document', formData.kbis_file);
        if (formData.identity_recto_file) formDataToSend.append('identity_document_recto', formData.identity_recto_file);
        if (formData.identity_verso_file) formDataToSend.append('identity_document_verso', formData.identity_verso_file);
        if (formData.identity_document_type) formDataToSend.append('identity_document_type', formData.identity_document_type);
        if (formData.rib_file) formDataToSend.append('rib_document', formData.rib_file);
        if (formData.profile_picture) formDataToSend.append('profile_picture', formData.profile_picture);
        if (formData.profession_is_other && formData.profession_other) {
          formDataToSend.append('profession_other', formData.profession_other);
        }

        const registeredUser = await registerUserWithFiles(formDataToSend);
        if (registeredUser) {
          toast.success("Inscription réussie ! Vos documents seront vérifiés sous 48h.");
          setTimeout(() => navigate("/verify-email"), 2000);
        }

      } else {
        // ============================================
        // PARTICULIER/ENTREPRISE
        // Si photo → FormData, sinon → JSON
        // ============================================

        if (formData.profile_picture) {
          // ⭐ CAS 1 : Avec photo → FormData
          const formDataToSend = new FormData();

          Object.keys(formData).forEach(key => {
            const value = formData[key as keyof typeof formData];
            if (value !== null && value !== undefined &&
              key !== 'profile_picture' &&
              key !== 'profile_picture_preview' &&
              key !== 'user_type' &&
              key !== 'kbis_file' &&
              key !== 'identity_recto_file' &&
              key !== 'identity_verso_file' &&
              key !== 'identity_document_type' &&
              key !== 'profession_is_other' &&
              key !== 'profession_other' &&
              key !== 'profession' &&
              key !== 'rib_file' &&
              key !== 'rib' &&
              key !== 'bic' &&
              key !== 'confirmPassword') {
              formDataToSend.append(key, String(value));
            }
          });

          formDataToSend.append('profession', professionFinal);
          formDataToSend.append('profession_is_other', String(!!formData.profession_is_other));
          formDataToSend.append('user_type', userType);
          formDataToSend.append('show_in_directory', String(!!formData.show_in_directory));
          if (idParent) formDataToSend.append('idParent', String(idParent));
          formDataToSend.append('profile_picture', formData.profile_picture);

          const registeredUser = await registerUser(formDataToSend);
          if (registeredUser) {
            toast.success("Inscription réussie !");
            setTimeout(() => navigate("/verify-email"), 2000);
          }

        } else {
          const payload = {
            ...formData,
            idParent,
            user_type: userType,
            profession: professionFinal,
            profession_is_other: !!formData.profession_is_other,
            profession_other: formData.profession_other || null,
            show_in_directory: formData.show_in_directory ? true : false,
          };

          const registeredUser = await registerUser(payload);
          if (registeredUser) {
            toast.success("Inscription réussie !");
            setTimeout(() => navigate("/verify-email"), 2000);
          }
        }
      }
    } catch (error: any) {
      console.error("Erreur lors de l'inscription :", error);

      if (error.message === "Code parrainage non valide") {
        toast.error("Code de parrainage invalide");
      } else if (error.message === "Mail déjà existant") {
        toast.error("Cette adresse email existe déjà");
      } else {
        toast.error(`Erreur lors de l'inscription : ${error.response?.data.message || error.message}`);
      }
    } finally {
      setStatusLoading(false);
    }
  };


  const renderSection = (title: string, icon: React.ReactNode, children: React.ReactNode) => (
    <div className="section-card mb-4">
      <div className="section-header">
        <div className="section-icon">
          {icon}
        </div>
        <h3>{title}</h3>
      </div>
      <div className="section-content">
        {children}
      </div>
    </div>
  )

  const renderCommonFields = () => (
    <>

      <div className="row">
        <FormSelect
          icon={<FontAwesomeIcon icon={gender === "male" ? faMale : faFemale} />}
          id="civility"
          value={formData.civility}
          onChange={(e) => {
            setGender(e.target.value === "monsieur" ? "male" : "female")
            handleChange(e)
          }}
          onBlur={() => handleBlur('civility')}
          options={[
            { value: "monsieur", label: "Monsieur" },
            { value: "madame", label: "Madame" }
          ]}
          placeholder="Civilité"
          required
          label="Civilité"
          error={touchedFields.has('civility') && !!formErrors.civility}
          errorMessage={formErrors.civility as string}
        />
        <FormSelect
          icon={<FontAwesomeIcon icon={faBriefcase} />}
          id="profession"
          value={formData.profession}
          onChange={handleChange}
          onBlur={() => handleBlur('profession')}
          options={professionOptions}
          placeholder="Sélectionnez votre profession"
          required
          label="Profession"
          error={touchedFields.has('profession') && !!formErrors.profession}
          errorMessage={formErrors.profession as string}
        />

      </div>

      <div className="row">
        <FormField
          icon={<FontAwesomeIcon icon={faUser} />}
          type="text"
          id="name"
          placeholder={userType === "personne" ? "Nom" : "Nom du Président/Gérant"}
          value={formData.name}
          onChange={handleChange}
          onBlur={() => handleBlur('name')}
          required
          label={userType === "personne" ? "Nom" : "Nom du Président/Gérant"}
          error={touchedFields.has('name') && !!formErrors.name}
          errorMessage={formErrors.name as string}
        />

        <FormField
          icon={<FontAwesomeIcon icon={faUser} />}
          type="text"
          id="firstname"
          placeholder="Prénom"
          value={formData.firstname}
          onChange={handleChange}
          onBlur={() => handleBlur('firstname')}
          required
          label="Prénom"
          error={touchedFields.has('firstname') && !!formErrors.firstname}
          errorMessage={formErrors.firstname as string}
        />
      </div>

      {formData.profession_is_other && (
        <div className="row">
          <FormField
            icon={<FontAwesomeIcon icon={faBriefcase} />}
            type="text"
            id="profession_other"
            placeholder="Précisez votre profession"
            value={formData.profession_other}
            onChange={handleChange}
            onBlur={() => handleBlur('profession_other')}
            className="col-md-12 mb-3"
            required
            label="Précisez votre profession"
            error={touchedFields.has('profession_other') && !!formErrors.profession_other}
            errorMessage={formErrors.profession_other as string}
          />
        </div>
      )}

      <div className="row">
        <FormField
          icon={<FontAwesomeIcon icon={faEnvelope} />}
          type="email"
          id="email"
          placeholder="Email"
          value={formData.email}
          onChange={handleChange}
          onBlur={() => handleBlur('email')}
          required
          label="Email"
          error={touchedFields.has('email') && !!formErrors.email}
          errorMessage={formErrors.email as string}
        />

        <FormField
          icon={<FontAwesomeIcon icon={faPhone} />}
          type="tel"
          id="number"
          placeholder="Téléphone"
          value={formData.number}
          onChange={handleChange}
          onBlur={() => handleBlur('number')}
          required
          label="Téléphone"
          error={touchedFields.has('number') && !!formErrors.number}
          errorMessage={formErrors.number as string}
        />
      </div>

      <div className="row">
        <FormField
          icon={<i className="bi bi-house-fill"></i>}
          type="text"
          id="adresse"
          placeholder="Adresse"
          value={formData.adresse}
          onChange={handleChange}
          onBlur={() => handleBlur('adresse')}
          required
          label="Adresse"
          error={touchedFields.has('adresse') && !!formErrors.adresse}
          errorMessage={formErrors.adresse as string}
        />

        <FormField
          icon={<FontAwesomeIcon icon={faMapMarkerAlt} />}
          type="text"
          id="postal_code"
          placeholder="Code Postal"
          value={formData.postal_code}
          onChange={handleChange}
          onBlur={() => handleBlur('postal_code')}
          required
          label="Code postal"
          error={touchedFields.has('postal_code') && !!formErrors.postal_code}
          errorMessage={formErrors.postal_code as string}
        />
      </div>

      <div className="row">
        <FormSelect
          icon={<i className="bi bi-pin-map-fill"></i>}
          id="country"
          value={formData.country}
          onChange={handleChange}
          onBlur={() => handleBlur('country')}
          options={cities.map(city => ({ value: city, label: city }))}
          placeholder="Ville"
          required
          label="Ville"
          error={touchedFields.has('country') && !!formErrors.country}
          errorMessage={formErrors.country as string}
        />

        <FormField
          icon={<FaTicketAlt />}
          type="text"
          id="code_parrainage"
          placeholder="Code Parrainage"
          value={formData.code_parrainage}
          onChange={handleChange}
          label="Code parrainage (optionnel)"
        />
      </div>

      <div className="row">
        <PasswordField
          icon={<FontAwesomeIcon icon={faLock} />}
          id="password"
          placeholder="Mot de passe"
          value={formData.password}
          onChange={handleChange}
          onBlur={() => handleBlur('password')}
          showPassword={showPassword}
          toggleVisibility={() => setShowPassword(!showPassword)}
          onPaste={preventCopyPaste}
          onCopy={preventCopyPaste}
          required
          label="Mot de passe"
          error={touchedFields.has('password') && !!formErrors.password}
          errorMessage={formErrors.password as string}
        />

        <PasswordField
          icon={<FontAwesomeIcon icon={faLock} />}
          id="confirmPassword"
          placeholder="Confirmer Mot de passe"
          value={formData.confirmPassword}
          onChange={handlePasswordConfirmation}
          onBlur={() => {
            handleBlur('confirmPassword');
            validatePasswordMatch();
          }}
          showPassword={showPassword2}
          toggleVisibility={() => setShowPassword2(!showPassword2)}
          onPaste={preventCopyPaste}
          onCopy={preventCopyPaste}
          required
          label="Confirmer mot de passe"
          error={touchedFields.has('confirmPassword') && !!formErrors.confirmPassword}
          errorMessage={formErrors.confirmPassword as string}
        />
      </div>
    </>
  )

  const renderCompanyFields = () => (
    <>
      <div className="row">
        <FormField
          icon={<i className="bi bi-credit-card-2-front-fill"></i>}
          type="text"
          id="code_SIREN"
          placeholder="Numéro SIREN"
          value={formData.code_SIREN}
          onChange={handleRCSInput}
          onBlur={() => handleBlur('code_SIREN')}
          required
          label="Numéro SIREN"
          error={touchedFields.has('code_SIREN') && !!formErrors.code_SIREN}
          errorMessage={formErrors.code_SIREN as string}
        />

        <FormField
          icon={<FontAwesomeIcon icon={faBuilding} />}
          type="text"
          id="raison_social"
          placeholder="Nom de la société"
          value={formData.raison_social}
          onChange={handleChange}
          onBlur={() => handleBlur('raison_social')}
          required
          label="Nom de la société"
          error={touchedFields.has('raison_social') && !!formErrors.raison_social}
          errorMessage={formErrors.raison_social as string}
        />
      </div>

      <div className="row">
        <FormField
          icon={<FontAwesomeIcon icon={faFileAlt} />}
          type="text"
          id="code_NAF"
          placeholder="Code NAF"
          value={formData.code_NAF}
          onChange={handleChange}
          label="Code NAF"
        />

        <FormField
          icon={<HiIdentification />}
          type="text"
          id="tv_intra"
          placeholder="TVA Intracommunautaire"
          value={formData.tv_intra}
          onChange={handleChange}
          onBlur={() => handleBlur('tv_intra')}
          required
          label="TVA Intracommunautaire"
          error={touchedFields.has('tv_intra') && !!formErrors.tv_intra}
          errorMessage={formErrors.tv_intra as string}
        />
      </div>
    </>
  )

  function validateForm() {
    const baseFields = userType === "personne"
      ? ["civility", "email", "name", "number", "firstname", "password", "confirmPassword", "adresse", "postal_code", "country", "profession"]
      : ["code_SIREN", "civility", "raison_social", "name", "adresse", "firstname", "postal_code", "number", "country", "email", "tv_intra", "password", "confirmPassword", "profession"]

    const emptyFields = baseFields.filter((field) => !formData[field as keyof typeof formData])

    if (formData.profession_is_other && !formData.profession_other) {
      emptyFields.push("profession_other")
    }

    if (userType === "professionnel") {
      if (!formData.kbis_file) emptyFields.push("kbis_file")
      if (!formData.identity_recto_file) emptyFields.push("identity_recto_file")
      if (formData.identity_document_type === 'cin' && !formData.identity_verso_file) {
        emptyFields.push("identity_verso_file")
      }
      if (!formData.rib_file) emptyFields.push("rib_file")
    }

    if (emptyFields.length > 0) {
      const fieldNames = emptyFields.map((field) => {
        const fieldMap: Record<string, string> = {
          civility: "Civilité",
          email: "Email",
          name: "Nom",
          number: "Téléphone",
          firstname: "Prénom",
          password: "Mot de passe",
          confirmPassword: "Confirmation du mot de passe",
          adresse: "Adresse",
          postal_code: "Code postal",
          country: "Ville",
          code_SIREN: "Numéro SIREN",
          raison_social: "Nom de la société",
          tv_intra: "TVA Intracommunautaire",
          profession: "Profession",
          profession_other: "Précisez la profession",
          kbis_file: "Extrait Kbis",
          identity_recto_file: "Pièce d'identité (recto)",
          identity_verso_file: "Pièce d'identité (verso)",
          rib_file: "Document RIB",
          rib_coordinates: "RIB (coordonnées bancaires)",
          bic_coordinates: "BIC (coordonnées bancaires)"
        }
        return fieldMap[field] || field
      })

      toast.error(
        <div>
          <p className="font-bold">Veuillez remplir tous les champs obligatoires :</p>
          <ul className="list-disc pl-4">
            {fieldNames.map((name, index) => (
              <li key={index}>{name}</li>
            ))}
          </ul>
        </div>,
      )
      return false
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas")
      return false
    }

    return true
  }

  const getStatusColor = () => {
    if (userType === "professionnel") return "#4bb0c0";
    if (userType === "entreprise") return "#F4A261";
    return "#9e9e9e";
  };

  const getStatusText = () => {
    if (userType === "professionnel") return "PROFESSIONNEL";
    if (userType === "entreprise") return "ENTREPRISE";
    return "PARTICULIER";
  };

  const handleFileChange = (info: any) => {
    const file = info.file.originFileObj || info.file;

    // Validation
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      toast.error('Vous ne pouvez uploader que des images !');
      return;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      toast.error('L\'image doit faire moins de 5MB !');
      return;
    }

    // Créer une URL de prévisualisation
    const reader = new FileReader();
    reader.onload = (e) => {
      setFormData(prev => ({
        ...prev,
        profile_picture: file,
        profile_picture_preview: e.target?.result as string
      }));
    };
    reader.readAsDataURL(file);
  };

  const customRequest = ({ file, onSuccess }: any) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };


  const handleRemovePhoto = () => {
    setFormData(prev => ({
      ...prev,
      profile_picture: null,
      profile_picture_preview: undefined
    }));
    toast.info("Photo de profil supprimée");
  };

  // ⭐ Nouvelle version du renderProfilePictureSection
  const renderProfilePictureSection = () => (
    <div className="section-card mb-4">
      <div className="section-header">
        <div className="section-icon">
          <i className="bi bi-person-circle"></i>
        </div>
        <h3>Photo de profil (optionnelle)</h3>
      </div>
      <div className="section-content">
        <div className="profile-picture-container" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          padding: '20px'
        }}>
          <div style={{ position: 'relative' }}>
            {formData.profile_picture_preview ? (
              <div style={{ position: 'relative' }}>
                <Avatar
                  size={150}
                  src={formData.profile_picture_preview}
                  style={{ border: '3px solid #2A919D' }}
                />
                <button
                  title="Supprimer la photo de profil"
                  type="button"
                  onClick={handleRemovePhoto}
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    padding: "10px",
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            ) : (
              <Avatar
                size={150}
                icon={<UserOutlined />}
                style={{ backgroundColor: '#e0e0e0', border: '3px solid #2A919D' }}
              />
            )}
          </div>

          <Upload
            customRequest={customRequest}
            onChange={handleFileChange}
            showUploadList={false}
            accept="image/jpeg,image/png,image/jpg"
            maxCount={1}
          >
            <Button
              icon={<CameraOutlined />}
              size="large"
              type="primary"
              style={{
                backgroundColor: '#2A919D',
                borderColor: '#2A919D',
                color: 'white',
              }}
            >
              <span style={{ color: "white " }}>{formData.profile_picture_preview ? 'Changer la photo' : 'Ajouter une photo'}</span>
            </Button>
          </Upload>

          <div style={{ textAlign: 'center', color: '#6c757d', fontSize: '14px' }}>
            <i className="bi bi-info-circle me-2"></i>
            Formats acceptés : JPG, PNG (max 5MB)
          </div>
        </div>
      </div>
    </div>
  );

  const proTooltipContent = (
    <div style={{ maxWidth: '300px', color: 'white' }}>
      <p style={{ margin: 0, fontWeight: 'bold', marginBottom: '8px', color: 'white' }}>Avantages du professionnel du droit :</p>
      <ul style={{ paddingLeft: '20px', marginBottom: 0, color: 'white' }}>
        <li style={{ color: 'white' }}>✓ Possibilité de faire payer directement vos clients</li>
        {/* <li style={{ color: 'white' }}>✓ Assistance dédiée prioritaire</li> */}
        <li style={{ color: 'white' }}>✓ Attestation instantanée</li>
        {/* <li style={{ color: 'white' }}>✓ Accès à des fonctionnalités exclusives</li> */}
      </ul>
    </div>
  );

  return (
    <>
      <ToastContainer />

      <RibModal
        visible={ribModalVisible}
        onCancel={() => setRibModalVisible(false)}
        onConfirm={handleRibConfirm}
        requireBic={true}
        zIndex={12010}
      />

      <div className="container-register">
        <div className="section-form">
          <div style={{ paddingBottom: '0px' }}>
            <a
              href="http://annonce-legale-officielle.fr/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={logo}
                className="logo-1 d-flex justify-content-center"
                alt="logo"
              />
            </a>
          </div>

          <div className="form-login px-register">
            <form onSubmit={handleSubmit} className="inscription" noValidate>
                {/* Sélection type de compte */}
                  <div className="section-card mb-4" style={{ marginTop: '40px' }}>
                    <div className="section-header">
                      <div className="section-icon">
                        <i className="bi bi-person-plus"></i>
                      </div>
                      <h3>Type de compte</h3>
                    </div>
                    <div className="section-content">
                      <div className="user-type-options">
                        <div className="user-type-option">
                          <input
                            type="radio"
                            name="userType"
                            id="personne"
                            value="personne"
                            checked={userType === "personne"}
                            onChange={(e) => handleUserTypeChange(e.target.value as "personne" | "entreprise" | "professionnel")}
                          />
                          <label htmlFor="personne">
                            <i className="bi bi-person" style={{ color: "#2A919D" }}></i> Particulier
                          </label>
                        </div>

                        <div className="user-type-option">
                          <input
                            type="radio"
                            name="userType"
                            id="entreprise-radio"
                            value="entreprise"
                            checked={userType === "entreprise"}
                            onChange={(e) => handleUserTypeChange(e.target.value as "personne" | "entreprise" | "professionnel")}
                          />
                          <label htmlFor="entreprise-radio">
                            <i className="bi bi-building" style={{ color: "#2A919D" }}></i> Entreprise
                          </label>
                        </div>

                        {/* ⭐ Option Pro visible uniquement quand entreprise est sélectionné */}
                        {showProOption && (
                          <div className="user-type-option pro-option">
                            <input
                              type="radio"
                              name="userType"
                              id="professionnel-radio"
                              value="professionnel"
                              checked={userType === "professionnel"}
                              onChange={(e) => handleUserTypeChange(e.target.value as "personne" | "entreprise" | "professionnel")}
                            />
                            <label htmlFor="professionnel-radio" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <i className="bi bi-shield-check" style={{ color: "#2A919D" }}></i> Professionnel 
                              {/* ⭐ Infobulle avec les avantages */}
                              <Tooltip title={proTooltipContent} color="#2A919D" placement="right">
                                <FontAwesomeIcon 
                                  icon={faInfoCircle} 
                                  style={{ 
                                    color: '#2A919D', 
                                    fontSize: '16px', 
                                    cursor: 'pointer',
                                    marginLeft: '5px'
                                  }} 
                                />
                              </Tooltip>
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                      <AnimatePresence>


                {(userType === "entreprise" || userType === "professionnel") && (
                  <motion.div
                    key="company-fields"
                    initial={{ opacity: 0, height: 0, overflow: "hidden" }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    {renderSection(
                      userType === "professionnel" ? "Informations professionnelles" : "Informations entreprise",
                      <i className="bi bi-building"></i>,
                      renderCompanyFields()
                    )}
                  </motion.div>
                )}
              </AnimatePresence>


              <AnimatePresence>
                {userType === "professionnel" && (
                  <motion.div
                    key="banking-section"
                    initial={{ opacity: 0, height: 0, overflow: "hidden" }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut", delay: 0.1 }}
                  >
                    <div className="section-card mb-4 banking-section">
                      <div className="section-header">
                        <div className="section-icon">
                          <i className="bi bi-bank2"></i>
                        </div>
                        <h3>Coordonnées bancaires</h3>
                      </div>
                      <div className="section-content">
                        <div className="banking-info-box">
                          <div className="banking-warning">
                            <i className="bi bi-info-circle me-2"></i>
                            <span>Ces informations sont utilisées pour les remboursements et paiements</span>
                          </div>
                          <div className="banking-form">
                            <Button
                              onClick={() => setRibModalVisible(true)}
                              icon={<i className="bi bi-plus-circle me-2"></i>}
                              size="large"
                              style={{ width: '100%', backgroundColor: '#2A919D', borderColor: '#2A919D', color: 'white  !important' }}
                            >
                              {formData.rib ? 'Modifier les coordonnées bancaires' : 'Ajouter les coordonnées bancaires'}
                            </Button>
                            {formData.rib && (
                              <div className="banking-details mt-3">
                                <div className="alert">
                                  <i className="bi bi-check-circle me-2"></i>
                                  <strong>RIB enregistré :</strong> {formatRIB(formData.rib)}
                                  {formData.bic && (
                                    <>
                                      <br />
                                      <strong>BIC :</strong> {formData.bic}
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {renderSection(
                "Informations générales",
                <i className="bi bi-person-lines-fill"></i>,
                renderCommonFields()
              )}



              {/* Section Documents pour professionnels */}
              <AnimatePresence>
                {userType === "professionnel" && (
                  <motion.div
                    key="documents-section"
                    initial={{ opacity: 0, height: 0, overflow: "hidden" }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut", delay: 0.2 }}
                  >
                    <>
                      {/* <div className="section-header">
                        <div className="section-icon">
                          <i className="bi bi-folder2-open"></i>
                        </div>
                        <h3>Documents à fournir</h3>
                      </div> */}
                      <div className="section-content" style={{ width: "100%" }}>
                        <div className="documents-required" >
                          <ProfessionalDocumentUpload onDocumentsChange={handleProfessionalDocuments} />
                        </div>
                      </div>
                    </>
                  </motion.div>
                )}
              </AnimatePresence>
              {renderProfilePictureSection()}
              <AnimatePresence>
                {(userType === "professionnel" || userType === "entreprise") && (
                  <div className="section-card mb-4">
                    <div className="section-header">
                      <div className="section-icon">
                        <i className="bi bi-eye"></i>
                      </div>
                      <h3>Annuaire professionnel</h3>
                    </div>

                    <div className="section-content">

                      {/* ── Consentement RGPD ── */}
                      <div style={{
                        padding: '16px 20px',
                        border: `1.5px solid ${formData.rgpd_directory_consent ? '#2A919D' : '#e8e8e8'}`,
                        borderRadius: 12,
                        background: formData.rgpd_directory_consent ? '#f0fafa' : '#fafafa',
                        marginBottom: 16,
                        transition: 'all 0.3s ease'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontWeight: 600 }}>
                          <i className="bi bi-shield-check" style={{ color: '#2A919D', fontSize: 18 }}></i>
                          <span>Consentement RGPD requis</span>
                          {formData.rgpd_directory_consent && (
                            <span className="badge ms-2" style={{ backgroundColor: '#2A919D' }}>✓ Accepté</span>
                          )}
                        </div>

                        <p style={{ fontSize: 13, color: '#666', marginBottom: 14, lineHeight: 1.6 }}>
                          Avant d'apparaître dans l'annuaire professionnel, vous devez accepter notre politique
                          de confidentialité concernant la collecte et l'utilisation de vos données personnelles.
                        </p>

                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                          <input
                            className="form-check-input mt-0"
                            type="checkbox"
                            id="rgpd_directory_consent"
                            checked={!!formData.rgpd_directory_consent}
                            style={{ flexShrink: 0, marginTop: 2, width: 16, height: 16, cursor: 'pointer' }}
                            onChange={(e) => {
                              const accepted = e.target.checked;
                              setFormData(prev => ({
                                ...prev,
                                rgpd_directory_consent: accepted,
                                show_in_directory: accepted ? prev.show_in_directory : false,
                              }));
                            }}
                          />
                          <label htmlFor="rgpd_directory_consent" style={{ fontSize: 13, lineHeight: 1.6, cursor: 'pointer', margin: 0 }}>
                            J'ai lu et j'accepte la{' '}
                            <a
                              href="https://annonce-legale-officielle.fr/politique-de-confidentialite/"
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              style={{ color: '#2A919D', fontWeight: 600, textDecoration: 'none' }}
                            >
                              politique de confidentialité (RGPD)
                            </a>
                            {' '}et j'accepte que mes informations professionnelles soient visibles dans l'annuaire.
                          </label>
                        </div>
                      </div>

                      {/* ── Visibilité annuaire — grisé si RGPD non accepté ── */}
                      <div style={{
                        opacity: formData.rgpd_directory_consent ? 1 : 0.45,
                        pointerEvents: formData.rgpd_directory_consent ? 'auto' : 'none',
                        transition: 'opacity 0.3s ease',
                        cursor: formData.rgpd_directory_consent ? 'auto' : 'not-allowed',
                        padding: '14px 20px',
                        border: '1.5px solid #e8e8e8',
                        borderRadius: 12,
                        background: '#fafafa',
                      }}>
                        <div className="form-check form-switch mb-0" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="show_in_directory"
                            checked={!!formData.show_in_directory}
                            disabled={!formData.rgpd_directory_consent}
                            onChange={(e) =>
                              setFormData(prev => ({ ...prev, show_in_directory: e.target.checked }))
                            }
                            style={{ width: '3em', height: '1.5em' }}
                          />
                          <label className="form-check-label ms-2" htmlFor="show_in_directory" style={{ fontWeight: 500 }}>
                            Apparaître dans l'annuaire professionnel
                          </label>
                        </div>
                        <small className="text-muted d-block mt-2" style={{ fontSize: 12 }}>
                          {formData.show_in_directory && formData.rgpd_directory_consent
                            ? "✓ Votre profil sera visible dans l'annuaire des professionnels."
                            : formData.rgpd_directory_consent
                              ? "Votre profil sera masqué de l'annuaire."
                              : "Acceptez la politique de confidentialité pour activer cette option."}
                        </small>
                      </div>

                    </div>
                  </div>
                )}
              </AnimatePresence>


              <button type="submit" className="btn btn-submit" disabled={statusLoading} style={{ color: '#fff', backgroundColor: getStatusColor(), borderColor: getStatusColor() }}>
                {statusLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Inscription en cours...
                  </>
                ) : "S'INSCRIRE"}
              </button>

              <div className="register-link mt-3">
                <p className="text-dark">
                  Vous avez déjà un compte ?
                  <Link to="/login" className="mx-2">
                    Se connecter
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>

        <div className="bg-section1" style={{ justifyContent: "center", textAlign: "center" }}>
          <div className="img-section1 img-animate1">
            <img src={test1 || "/placeholder.svg"} alt="gif" className="img-fluid w-100" />
          </div>
          <div className="text-section1">
            <h1 className="font-1">
              Annonce Legale <br /> Officielle
            </h1>
            <p className="font-2">
              Bénéficiez d'une publication instantanée dans toute la France, grâce à notre plateforme sécurisée en ligne, disponible 24/7 et adaptée à tout type d'annonce, des plus simples aux plus complexes.
            </p>
          </div>
        </div>
      </div>
    </>
  )
}

export default Register;